/**
 * 제안서 API 서비스
 * 제안서 CRUD, 맥락표 생성, 본문 생성, 내보내기
 */

import apiClient, { PaginatedResponse } from './api';

// ============== Interfaces ==============

export type ProposalStatus = 'draft' | 'writing' | 'reviewing' | 'submitted' | 'selected' | 'rejected';

export interface Proposal {
  id: string;
  title: string;
  document_type: string;
  status: ProposalStatus;
  progress: number;
  grant_id?: string;
  grant_title?: string;
  company_id?: string;
  context_table?: ContextTableData;
  content?: string;
  content_html?: string;
  summary?: string;
  total_budget?: string;
  government_fund?: string;
  private_fund?: string;
  evaluation_score?: number;
  evaluation_result?: Record<string, unknown>;
  ai_model_used?: string;
  created_at: string;
  updated_at: string;
}

export interface ContextItem {
  key: string;
  value: string;
  category: string;
  source: string;
  is_required: boolean;
  ai_generated: boolean;
}

export interface ContextTableData {
  id: string;
  proposal_id: string;
  items: ContextItem[];
  completion_rate: number;
}

export interface ProposalStyle {
  key: string;
  name: string;
  description: string;
  structure_pattern?: string[];
}

export interface QualityMetrics {
  completeness: number;
  structure: number;
  style_adherence: number;
  overall: number;
}

export interface GenerateContextTableRequest {
  grant_id?: string;
  portfolio_ids?: string[];
  style_key?: string;
  company_info?: Record<string, unknown>;
}

export interface GenerateProposalRequest {
  proposal_id?: string;
  context_table_id?: string;
  style_key?: string;
  sections?: string[];
}

export interface ExportProposalRequest {
  proposal_id: string;
  format: 'docx' | 'pdf' | 'md' | 'html';
  include_cover?: boolean;
  include_toc?: boolean;
}

export interface CreateProposalRequest {
  title: string;
  document_type: string;
  grant_id?: string;
  company_id?: string;
}

// ============== Service ==============

class ProposalService {
  async getProposals(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<PaginatedResponse<Proposal>> {
    return apiClient.get('/api/proposals', params);
  }

  async getProposal(id: string): Promise<Proposal> {
    return apiClient.get(`/api/proposals/${id}`);
  }

  async createProposal(data: CreateProposalRequest): Promise<Proposal> {
    return apiClient.post('/api/proposals', data as unknown as Record<string, unknown>);
  }

  async updateProposal(id: string, data: Partial<Proposal>): Promise<Proposal> {
    return apiClient.put(`/api/proposals/${id}`, data as unknown as Record<string, unknown>);
  }

  async deleteProposal(id: string): Promise<void> {
    return apiClient.delete(`/api/proposals/${id}`);
  }

  async generateContextTable(data: GenerateContextTableRequest): Promise<ContextTableData> {
    return apiClient.post('/api/proposal/context-table/enhanced', data as unknown as Record<string, unknown>);
  }

  async generateProposalBody(data: GenerateProposalRequest): Promise<{
    content: string;
    content_html: string;
    quality_metrics: QualityMetrics;
    references: string[];
  }> {
    return apiClient.post('/api/proposal/generate/enhanced', data as unknown as Record<string, unknown>);
  }

  async exportProposal(data: ExportProposalRequest): Promise<void> {
    const filename = `proposal_${data.proposal_id}.${data.format}`;
    return apiClient.download(
      `/api/proposal/export/document?proposal_id=${data.proposal_id}&format=${data.format}`,
      filename
    );
  }

  async getAvailableStyles(): Promise<ProposalStyle[]> {
    return apiClient.get('/api/proposal/styles/available');
  }

  async getStyle(styleKey: string): Promise<ProposalStyle> {
    return apiClient.get(`/api/proposal/styles/${styleKey}`);
  }
}

export const proposalService = new ProposalService();
export default proposalService;
